import type { DefaultValues } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import type { IContentBlockFormBodyProps } from '~/components/molecules/ContentBlock';
import type { IContainerProps } from '~/components/utils/Container';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { getRegionFromPath, Navigate } from '~/components/utils/Navigation';
import { useFormAutoFocus } from '~/hooks/useFormAutoFocus';
import { usePublicInvitationQuery } from '~/lib/api/modules/Invitations/hooks/usePublicInvitationQuery';
import { OrganizationInvitationResponseForm } from './OrganizationInvitationResponseForm';

export type IInvitationResponseFormValues = {
  challengeCode?: string;
};

export type IInvitationResponseFormProps = IContainerProps &
  Omit<
    IContentBlockFormBodyProps<IInvitationResponseFormValues>,
    'form' | 'children' | 'onSubmit' | 'submitting' | 'cancelUrl' | 'onCancel'
  > & {
    invitationId: string;
    defaultValues?: DefaultValues<IInvitationResponseFormValues>;
    cancelUrl?: string;
  };

export const InvitationResponseForm: React.FC<IInvitationResponseFormProps> = (
  props,
) => {
  const { sx, invitationId, defaultValues, cancelUrl, ...other } = props;
  const { t } = useTranslation();
  const location = useLocation();
  const appRegion = getRegionFromPath(location.pathname.split('/')[1]);
  const invitationQuery = usePublicInvitationQuery(
    appRegion
      ? {
          invitationId,
          appRegion,
        }
      : undefined,
  );
  const invitation = invitationQuery.data;
  const form = useForm<IInvitationResponseFormValues>({
    defaultValues,
  });
  useFormAutoFocus({ form, name: 'challengeCode' });

  if (!appRegion) {
    return <Navigate to="/" />;
  }

  return (
    <ContentBlock sx={sx}>
      <ContentBlock.Suspense
        loading={invitationQuery.isLoading}
        error={invitationQuery.error}
        first
      >
        {invitation ? (
          invitation.target.type === 'organization' ? (
            <OrganizationInvitationResponseForm
              invitation={invitation}
              appRegion={appRegion}
              defaultValues={defaultValues}
              form={form}
              cancelUrl={cancelUrl}
              {...other}
            />
          ) : (
            <ContentBlock.Body first>
              <ErrorMessage
                error={t('InvitationResponseForm.unsupported', {
                  type: invitation.target.type,
                })}
              />
            </ContentBlock.Body>
          )
        ) : null}
      </ContentBlock.Suspense>
    </ContentBlock>
  );
};
